import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Flex, Input, Row, Skeleton, Typography, message } from "antd";
import axios from "axios";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntersection } from "react-use";
import PickerListFooter from "../../components/ClientPicker/PickerListFooter.component";
import { TabType, ViewType } from "../../components/CreateNoteModal/AITemplateModal/types";
import TemplateCard from "../../components/TemplateCard/template-card.component";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { type Template, getTemplatesPaged } from "../../service/templates.service";
import {
  setAITemplateModalState,
  setCustomTemplateSelectedTab,
  setNotesViewVisible,
  setSelectedView,
} from "../../slices/appStateSlice";
import styles from "./template.module.scss";

const { Text } = Typography;

// Divides evenly into the grid's 2/3/4-column breakpoints so pages land on full rows.
const PAGE_SIZE = 24;

export const TemplatesPage: React.FC = () => {
  const componentName = "template";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const isNotesViewVisible = useAppSelector((state) => state.appState.isNotesViewVisible);

  // Cursor for the next page; always advanced from the response's next_offset.
  const offsetRef = useRef(0);
  // One controller for ALL fetches: a new fetch (search reset or next page) aborts its
  // predecessor so an out-of-order response can't repaint the wrong list.
  const abortRef = useRef<AbortController | null>(null);
  // Synchronous double-fetch guard: state flags update async, so the sentinel effect can
  // re-run before loadingMore is visible.
  const inFlightRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(sentinelRef, { root: null, rootMargin: "0px", threshold: 0 });

  const fetchPage = useCallback(async (offset: number, query: string, reset: boolean) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;
    if (reset) {
      setLoading(true);
      setLoadingMore(false);
      setTemplates([]);
      setHasMore(false);
    } else {
      setLoadingMore(true);
    }
    setLoadError(false);
    try {
      const page = await getTemplatesPaged(
        { offset, page_size: PAGE_SIZE, ...(query ? { search: query } : {}) },
        controller.signal,
      );
      offsetRef.current = page.next_offset ?? offset + page.results.length;
      setTemplates((prev) => {
        if (reset) return page.results;
        // Dedupe by slug: a template created between pages shifts offset windows and
        // would otherwise duplicate a row (slug is the React key).
        const seen = new Set(prev.map((t) => t.slug));
        return [...prev, ...page.results.filter((t) => !seen.has(t.slug))];
      });
      setHasMore(page.hasMore);
      setLoading(false);
      setLoadingMore(false);
      inFlightRef.current = false;
    } catch (error) {
      if (axios.isCancel(error)) return; // superseded by a newer fetch — let it drive the state
      console.error("Error fetching templates:", error);
      inFlightRef.current = false;
      setLoading(false);
      setLoadingMore(false);
      if (reset) {
        message.error("Failed to load templates");
      } else {
        setLoadError(true);
      }
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only view setup + unmount abort
  useEffect(() => {
    dispatch(setSelectedView(ViewType.TEMPLATE));
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Covers the initial load, every (debounced) search change, and clearing the search.
  useEffect(() => {
    fetchPage(0, debouncedQuery, true);
  }, [debouncedQuery, fetchPage]);

  // Load the next page when the sentinel below the grid enters the viewport. Re-runs when
  // loadingMore flips back off, so it chains pages if the sentinel never leaves view.
  useEffect(() => {
    if (!intersection?.isIntersecting) return;
    if (loading || loadingMore || !hasMore || loadError || inFlightRef.current) return;
    // The observer entry can be stale: appended cards push the sentinel below the fold,
    // but the IO's "false" update arrives async, after this effect re-runs. Re-check the
    // real position so a stale "true" can't chain-load pages the user never scrolled to.
    const rect = sentinelRef.current?.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (!rect || rect.top > viewportHeight || rect.bottom < 0) return;
    fetchPage(offsetRef.current, debouncedQuery, false);
  }, [intersection?.isIntersecting, loading, loadingMore, hasMore, loadError, debouncedQuery, fetchPage]);

  const retryLoadMore = () => fetchPage(offsetRef.current, debouncedQuery, false);

  const handleUseTemplate = (slug: string): void => {
    //scroll to top
    const topContainer = document.getElementById("topContainer");
    if (topContainer) {
      topContainer.scrollIntoView({ behavior: "smooth" });
    }
    navigate(`/templates/${slug}`);
  };

  const handleSearch = (value: string): void => {
    setSearchQuery(value);
  };

  const createTemplate = (): void => {
    if (isNotesViewVisible) {
      dispatch(setNotesViewVisible(false));
    }
    dispatch(setSelectedView(ViewType.DASHBOARD));
    navigate("/home");
    dispatch(setAITemplateModalState(true));
    dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
  };

  const EmptyState = () => {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((_, index) => (
          <Col key={index} span={24} md={6} lg={6}>
            <Card>
              <Skeleton active />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const handleBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  return (
    <div id="topContainer" className={styles[`${componentName}__container`]}>
      <div style={{ background: "#fff", padding: "16px 24px 0", textAlign: "left" }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ color: "#6b7280", paddingLeft: 0 }}
        >
          Back
        </Button>
      </div>
      <div className={`${styles[`${componentName}__bg`]}`}>
        <Row style={{ marginBottom: "2rem" }}>
          <Col span={24} style={{ textAlign: "center", padding: "0 2rem" }}>
            <Flex justify="center">
              <svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_3694_11910)">
                  <rect x="2" y="1" width="64" height="64" rx="16" fill="white" />
                  <rect x="2.75" y="1.75" width="62.5" height="62.5" rx="15.25" stroke="#C7D2FE" stroke-width="1.5" />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M22 22.5C22 21.1187 23.12 20 24.5 20H31.5C32.8813 20 34 21.12 34 22.5V40C34 41.5913 33.3679 43.1174 32.2426 44.2426C31.1174 45.3679 29.5913 46 28 46C26.4087 46 24.8826 45.3679 23.7574 44.2426C22.6321 43.1174 22 41.5913 22 40V22.5ZM28 41.5C28.3978 41.5 28.7794 41.342 29.0607 41.0607C29.342 40.7794 29.5 40.3978 29.5 40C29.5 39.6022 29.342 39.2206 29.0607 38.9393C28.7794 38.658 28.3978 38.5 28 38.5C27.6022 38.5 27.2206 38.658 26.9393 38.9393C26.658 39.2206 26.5 39.6022 26.5 40C26.5 40.3978 26.658 40.7794 26.9393 41.0607C27.2206 41.342 27.6022 41.5 28 41.5Z"
                    fill="url(#paint0_linear_3694_11910)"
                  />
                  <path
                    d="M33.292 46.0005H45.5C46.8813 46.0005 48 44.8805 48 43.5005V36.5005C48 35.1192 46.88 34.0005 45.5 34.0005H45.3133L33.6573 45.6578C33.5373 45.7765 33.4173 45.8912 33.292 46.0005ZM35.984 40.5005L44.616 31.8685C45.0846 31.3997 45.3478 30.764 45.3478 30.1012C45.3478 29.4383 45.0846 28.8026 44.616 28.3338L39.6667 23.3832C39.1979 22.9146 38.5622 22.6514 37.8993 22.6514C37.2365 22.6514 36.6008 22.9146 36.132 23.3832L35.9987 23.5152V40.0005C35.9987 40.1685 35.9947 40.3352 35.9853 40.5005H35.984Z"
                    fill="url(#paint1_linear_3694_11910)"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_d_3694_11910"
                    x="0"
                    y="0"
                    width="68"
                    height="68"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                  >
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3694_11910" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3694_11910" result="shape" />
                  </filter>
                  <linearGradient
                    id="paint0_linear_3694_11910"
                    x1="28"
                    y1="20"
                    x2="28"
                    y2="46"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#6366F1" />
                    <stop offset="1" stop-color="#EFC9FF" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_3694_11910"
                    x1="40.646"
                    y1="22.6514"
                    x2="40.646"
                    y2="46.0005"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#6366F1" />
                    <stop offset="1" stop-color="#EFC9FF" />
                  </linearGradient>
                </defs>
              </svg>
            </Flex>
            <div
              style={{
                fontSize: "2.5rem",
                color: "#000",
                fontWeight: "600",
                lineHeight: "2.5rem",
                margin: "1rem",
              }}
            >
              Template Gallery
            </div>
            <Text className={styles[`${componentName}__main-subtitle`]}>
              Prebuilt templates for behavioral health clinicians to streamline documentation. Use our templates as-is,
              customize them to fit your practice or{" "}
              <span
                style={{
                  color: "#4F46E5",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
                onClick={createTemplate}
              >
                create your own
              </span>
              .
            </Text>
          </Col>
        </Row>

        <Row justify="center" style={{ padding: "0 2rem" }}>
          <Col xs={24} sm={20} md={16} lg={10}>
            <div className={styles[`${componentName}__main-input-container`]}>
              <Input
                style={{ flex: 1 }}
                placeholder="Search templates..."
                prefix={<SearchOutlined style={{ color: "#666" }} />}
                onChange={(e) => handleSearch(e.target.value)}
                value={searchQuery}
                allowClear
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className={styles[`${componentName}__container-list`]}>
        {loading ? (
          <EmptyState />
        ) : templates.length === 0 ? (
          <Row justify="center" style={{ marginTop: "4rem" }}>
            <Col>
              <Text type="secondary" style={{ fontSize: "1.1rem" }}>
                No templates found matching your criteria.
              </Text>
            </Col>
          </Row>
        ) : (
          <>
            <Row
              gutter={[24, 24]}
              style={{
                maxWidth: "1200px",
                margin: "0 auto",
                justifyContent: "flex-start",
              }}
            >
              {templates.map((template, index) => (
                <Col key={template.slug} span={24} md={12} lg={8} xl={6}>
                  <TemplateCard
                    index={index}
                    key={template.slug}
                    title={template.name}
                    content={template.content}
                    description={template.description}
                    slug={template.slug}
                    onUse={() => handleUseTemplate(template.slug)}
                    onCardClick={() => handleUseTemplate(template.slug)}
                  />
                </Col>
              ))}
            </Row>
            <PickerListFooter loadingMore={loadingMore} error={loadError} retry={retryLoadMore} />
          </>
        )}
        {/* Always mounted: useIntersection only attaches its observer when the ref is set
            at effect time, so a conditionally-rendered sentinel could miss observation. */}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
};

export default TemplatesPage;
